import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAudit } from '@/contexts/AuditContext';
import { Shield, Users, Plus, Edit, Trash2, Search, AlertCircle, Copy, Loader2 } from 'lucide-react';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';
import { DataModeToggle } from '@/components/DataModeToggle';
import { getUserRoleService, UserRoleListItem } from '@/services/userRoleService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Role = UserRoleListItem & { description: string };

interface RolesPermissionsPageProps {
  onNavigate?: (page: string) => void;
}

export const RolesPermissionsPage: React.FC<RolesPermissionsPageProps> = ({ onNavigate }) => {
  const { getScreenMode } = useDataMode();
  const { currentCompany } = useCompany();
  const audit = useAudit();
  const currentMode = getScreenMode('settings');
  const service = getUserRoleService(currentMode);
  const serviceRef = useRef(service);
  serviceRef.current = service;

  const [searchQuery, setSearchQuery] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [roleToDuplicate, setRoleToDuplicate] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await serviceRef.current.getAllUserRoles();
      setRoles(data.map(r => ({ ...r, description: r.description ?? '' })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles, currentMode, currentCompany?.id]);

  useEffect(() => {
    const handleRolesUpdated = () => { fetchRoles(); };
    window.addEventListener('rolesUpdated', handleRolesUpdated);
    return () => { window.removeEventListener('rolesUpdated', handleRolesUpdated); };
  }, [fetchRoles]);

  const filteredRoles = useMemo(
    () =>
      roles.filter(
        (role) =>
          role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          role.description.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [roles, searchQuery],
  );

  const handleEditRole = (roleId: number) => {
    onNavigate?.(`/settings/roles-permissions/edit/${roleId}`);
  };

  const handleCreateRole = () => {
    onNavigate?.(`/settings/roles-permissions/create`);
  };

  const handleDuplicateRole = (role: Role) => {
    setRoleToDuplicate(role);
    setDuplicateDialogOpen(true);
  };

  const handleDeleteRole = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!roleToDelete) return;

    setIsDeleting(true);
    try {
      await serviceRef.current.deleteUserRole(roleToDelete.id);
      setRoles(prev => prev.filter((r) => r.id !== roleToDelete.id));
      setDeleteDialogOpen(false);
      audit.log({
        user: 'Current User',
        category: 'Roles & Permissions',
        action: 'Delete',
        entity: roleToDelete.name,
        changes: [{ field: 'Role', before: roleToDelete.name, after: null }],
      });
      setRoleToDelete(null);
      window.dispatchEvent(new Event('rolesUpdated'));
    } catch {
      setError('Failed to delete role. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [roleToDelete, audit]);

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleConfirmDuplicate = useCallback(async () => {
    if (!roleToDuplicate) return;

    try {
      const newRole = await serviceRef.current.createUserRole({
        name: `${roleToDuplicate.name} (Copy)`,
        description: roleToDuplicate.description,
        isActive: true,
      });
      setRoles(prev => [...prev, {
        id: newRole.id,
        name: newRole.name,
        description: newRole.description ?? '',
        isSystem: newRole.isSystem,
        isActive: newRole.isActive,
        userCount: newRole.userCount,
        createdAt: newRole.createdAt,
      }]);
      setDuplicateDialogOpen(false);
      audit.log({
        user: 'Current User',
        category: 'Roles & Permissions',
        action: 'New',
        entity: newRole.name,
        changes: [{ field: 'Duplicated from', before: null, after: roleToDuplicate.name }],
      });
      setRoleToDuplicate(null);
      window.dispatchEvent(new Event('rolesUpdated'));
    } catch {
      setError('Failed to duplicate role. Please try again.');
    }
  }, [roleToDuplicate, audit]);

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
    setRoleToDuplicate(null);
  };

  const loadRoles = () => {
    fetchRoles();
  };

  return (
    <div className="space-y-6 p-6 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <Shield className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">Roles &amp; Permissions</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Manage user roles and access permissions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <DataModeToggle screen="settings" />
          <Button onClick={handleCreateRole}>
            <Plus className="h-4 w-4 mr-2" />
            Create Role
          </Button>
        </div>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>User Roles</CardTitle>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Users</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading roles...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-destructive">
                      <AlertCircle className="h-5 w-5" />
                      <span>{error}</span>
                      <Button variant="outline" size="sm" onClick={loadRoles}>
                        Try Again
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No roles found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell className="text-muted-foreground">{role.description}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{role.userCount}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={role.isActive ? 'default' : 'secondary'}
                        className={role.isActive ? 'bg-success-500 hover:bg-success-600' : ''}
                      >
                        {role.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-xs">
                        {role.isSystem ? 'Platform' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditRole(role.id)}
                          title="Edit role permissions"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDuplicateRole(role)}
                          title="Duplicate role"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={role.isSystem}
                          onClick={() => handleDeleteRole(role)}
                          title={role.isSystem ? 'Platform roles cannot be deleted' : 'Delete role'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roles.length}</div>
            <p className="text-xs text-muted-foreground">Active role definitions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {roles.reduce((sum, role) => sum + role.userCount, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Users with assigned roles</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platform Roles</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {roles.filter((role) => role.isSystem).length}
            </div>
            <p className="text-xs text-muted-foreground">Default system roles</p>
          </CardContent>
        </Card>
      </div>

      {/* Duplicate Role Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Duplicate Role</DialogTitle>
            <DialogDescription>
              Create a new role based on &quot;{roleToDuplicate?.name}&quot;. You&apos;ll be able to
              configure the name, description, and permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start gap-3">
                <Copy className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium">Duplicating from:</p>
                  <p className="text-sm font-semibold">{roleToDuplicate?.name}</p>
                  <p className="text-xs text-muted-foreground">{roleToDuplicate?.description}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium">What will be copied:</p>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>All permission settings and access levels</li>
                    <li>Scheduling configurations</li>
                    <li>Resource and booking permissions</li>
                    <li>Management and integration settings</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2">
                    You&apos;ll be able to modify everything before saving the new role.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDuplicate}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDuplicate}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role &quot;{roleToDelete?.name}&quot;? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {roleToDelete && roleToDelete.userCount > 0 && (
            <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Warning</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    This role has {roleToDelete.userCount} user(s) assigned. Please reassign all
                    users to another role before deleting.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting || (roleToDelete !== null && roleToDelete.userCount > 0)}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
